import React, { useMemo, useState } from 'react';
import ContainerHeader from '../components/ContainerHeader';
import {
  Button,
  Tab,
  Tabs,
  Chip,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { makeStyles } from '@material-ui/core/styles';
import useWoodChipSampleController from './CustomHooks/useWoodchipSampleController';
import AddEditWoodchipModal from './components/AddEditWoodChipSample';
import { GroupedSampleTable } from './components/GroupedSampleTable';
import CommonSearchBar from './components/CommonSearchBar';
import PendingSampleTable from './components/PendingSampleTable';
import { parseCustomerMcExcel, uploadCustomerMcSamples, CustomerMcValidationError } from './Data/customerMCUpload';
import {
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const TAB_KEYS = ['Pending', 'Retest', 'Completed'];

const useStyles = makeStyles(() => ({
  tabsRoot: {
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  tab: {
    minWidth: 140,
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.9rem',
  },
  badge: {
    marginLeft: 8,
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#fff',
  },
  tableContainer: {
    height: '100%',
    overflow: 'auto',
    borderRadius: 0,
    boxShadow: 'none',
    border: '1px solid #e0e0e0',
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: '0.78rem',
    color: '#424242',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': { borderRight: 'none' },
  },
  primaryRow: {
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#f9f9f9' },
    cursor: 'default',
  },
  primaryRowRetest: {
    backgroundColor: '#fff8e1',
    '&:hover': { backgroundColor: '#fff3cd' },
  },
  historyRow: {
    backgroundColor: '#fafafa',
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  historyRowRetest: {
    backgroundColor: '#fff8e1',
    opacity: 0.75,
    '&:hover': { backgroundColor: '#fff3cd' },
  },
  tableCell: {
    fontSize: '0.82rem',
    padding: '6px 12px',
    borderRight: '1px solid #eeeeee',
    '&:last-child': { borderRight: 'none' },
  },
  historyCellIndented: {
    fontSize: '0.8rem',
    color: '#666',
    padding: '5px 12px',
    borderRight: '1px solid #eeeeee',
    '&:last-child': { borderRight: 'none' },
  },
  expandCell: {
    width: 40,
    padding: '0 4px',
    borderRight: '1px solid #eeeeee',
  },
  docketBtn: {
    color: '#1976d2',
    fontWeight: 700,
    fontSize: '0.82rem',
    padding: '2px 6px',
    minWidth: 'unset',
    textTransform: 'none',
  },
  testNoBadge: {
    display: 'inline-block',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  testNoBadgeOld: {
    display: 'inline-block',
    backgroundColor: '#f5f5f5',
    color: '#757575',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  retestNoBadge: {
    display: 'inline-block',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  historyLabel: {
    fontSize: '0.72rem',
    color: '#9e9e9e',
    fontStyle: 'italic',
    paddingLeft: 8,
  },
  collapseCell: {
    padding: 0,
    borderBottom: 'none',
  },
  innerTable: {
    width: '100%',
  },
  innerTableRow: {
    borderTop: '1px dashed #e0e0e0',
  },
}));

const BADGE_COLORS = {
  Pending: '#1976d2',
  Completed: '#388e3c',
  Retest: '#f57c00',
  'Customer MC': '#6a1b9a',
};

export const matchesSearch = (row, term) => {
  if (!term) return true;
  const haystack = Object.values(row)
    .filter(v => v !== null && v !== undefined && typeof v !== 'object')
    .join(' ')
    .toLowerCase();
  return haystack.includes(term.trim().toLowerCase());
};

export const filterRows = (rows = [], term = '') => {
  if (!term) return rows;
  return rows.filter(row => matchesSearch(row, term));
};

const WoodChipSamplePage = props => {
  const classes = useStyles();
  const {
    pendingSamples,
    completedSamples,
    retestSamples,
    dockets,
    woodchipSampleData,
    handleWoodchipAddEditModalOpen,
    selectedWoodchipSampleData,
    openWoodchipAddEditModal,
    setOpenWoodchipAddEditModal,
    addHandleWoodChipSampleDataSubmit,
    handleMarkForRetest,
    isRetestMode,
    activeTab,
    setActiveTab,
    changeSampleStatus,
  } = useWoodChipSampleController();

  const [retestConfirmOpen, setRetestConfirmOpen] = useState(false);
  const [pendingRetestRow, setPendingRetestRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [mcPreviewRows, setMcPreviewRows] = useState(null); // array once parsed, null = no preview open
  const [mcPreviewFileName, setMcPreviewFileName] = useState('');

  // ── Customer MC upload state ─────────────────────────────────────────────
  const fileInputRef = React.useRef(null);
  const [mcUploading, setMcUploading] = useState(false);
  const [mcResultDialog, setMcResultDialog] = useState(null);
  const [mcAckInvalid, setMcAckInvalid] = useState(false);

  // File can contain rows for several different customers, so we go
  // straight to the file picker — no "which customer" prompt.
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const mcHardErrorCount = mcPreviewRows?.filter(r => r.hardErrors.length > 0).length ?? 0;
  const mcSoftErrorCount = mcPreviewRows?.filter(r => r.softErrors.length > 0).length ?? 0;

  const handleMcFileSelected = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset input regardless of outcome

    try {
      const payload = await parseCustomerMcExcel(file); // parses + validates headers, throws on either failure
      setMcPreviewFileName(file.name);
      setMcAckInvalid(false);
      setMcPreviewRows(payload); // opens the preview modal
    } catch (err) {
      const message = err instanceof CustomerMcValidationError ? err.message : err.message || 'Could not read that file.';
      setMcResultDialog({ type: 'error', message });
    }
  };

  const cancelMcPreview = () => {
    setMcPreviewRows(null);
    setMcPreviewFileName('');
  };

  const confirmMcUpload = async () => {
    const payload = mcPreviewRows;

    setMcPreviewRows(null);
    setMcUploading(true);

    try {
      const response = await uploadCustomerMcSamples(payload);
      const total = response?.total ?? payload.length;
      const succeeded = response?.succeeded ?? 0;
      const failed = response?.failed ?? (response ? 0 : payload.length);
      const failedRows = Array.isArray(response?.failedRows) ? response.failedRows : [];

      const groups = new Map();
      failedRows.forEach(f => {
        const key = f.error || 'Unknown error';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(f.row?.customerDocketNumber || '(blank)');
      });

      setMcResultDialog({
        type: failed === 0 ? 'success' : 'error',
        total,
        succeeded,
        failed,
        groups: Array.from(groups.entries()).map(([error, dockets]) => ({ error, dockets })),
      });
    } catch (err) {
      console.error('Customer MC upload failed:', err);
      setMcResultDialog({
        type: 'error',
        message: err.message || 'Upload failed. Check the console/network tab for details.',
      });
    } finally {
      setMcUploading(false);
      setMcPreviewFileName('');
    }
  };

  const handleTabChange = (_, newValue) => setActiveTab(TAB_KEYS[newValue]);

  const filteredPending = useMemo(() => filterRows(pendingSamples, searchTerm), [pendingSamples, searchTerm]);
  const filteredCompleted = useMemo(() => filterRows(completedSamples, searchTerm), [completedSamples, searchTerm]);
  const filteredRetest = useMemo(() => filterRows(retestSamples, searchTerm), [retestSamples, searchTerm]);

  const activeTabIndex = TAB_KEYS.indexOf(activeTab);

  const counts = {
    Pending: filteredPending.length,
    Completed: new Set(filteredCompleted.map(x => x.wH_DocketNumber)).size,
    Retest: new Set(filteredRetest.map(x => x.wH_DocketNumber)).size,
  };

  const tabLabel = key => (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      {key}
      {counts[key] > 0 && (
        <Chip label={counts[key]} size="small" className={classes.badge} style={{ backgroundColor: BADGE_COLORS[key] }} />
      )}
    </span>
  );

  const openRetestConfirm = rowData => {
    setPendingRetestRow(rowData);
    setRetestConfirmOpen(true);
  };
  const confirmRetest = async () => {
    if (pendingRetestRow) await handleMarkForRetest(pendingRetestRow);
    setRetestConfirmOpen(false);
    setPendingRetestRow(null);
  };
  const cancelRetest = () => {
    setRetestConfirmOpen(false);
    setPendingRetestRow(null);
  };

  return (
    <div className="app-wrapper siteWrapper">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <ContainerHeader match={props.match} title="Woodchip Samples" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CommonSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search across all tabs (docket, customer, SP number...)"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={handleMcFileSelected}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={mcUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleUploadClick}
            disabled={mcUploading}>
            {mcUploading ? 'Uploading...' : 'Upload Customer MC'}
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabsRoot}>
        <Tab label={tabLabel('Pending')} className={classes.tab} />
        <Tab label={tabLabel('Retest')} className={classes.tab} />
        <Tab label={tabLabel('Completed')} className={classes.tab} />
      </Tabs>

      <div
        style={{
          height: 'calc(100vh - 190px)',
          width: '100%',
          paddingTop: '0.5rem',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {activeTab === 'Pending' && (
          <PendingSampleTable
            samples={filteredPending}
            searchTerm={searchTerm}
            onStartSampling={row => handleWoodchipAddEditModalOpen(row, false)}
            classes={classes}
          />
        )}
        {activeTab === 'Completed' && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <GroupedSampleTable
              samples={filteredCompleted}
              isRetest={false}
              onMarkRetest={openRetestConfirm}
              searchTerm={searchTerm}
            />
          </div>
        )}
        {activeTab === 'Retest' &&
          (filteredRetest.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography variant="body1" color="textSecondary">
                {searchTerm ? 'No matching retest samples.' : 'No samples pending retest.'}
              </Typography>
            </Box>
          ) : (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <GroupedSampleTable
                samples={filteredRetest}
                isRetest={true}
                onActionClick={row => handleWoodchipAddEditModalOpen(row, true)}
              />
            </div>
          ))}
      </div>

      <AddEditWoodchipModal
        open={openWoodchipAddEditModal}
        setOpen={setOpenWoodchipAddEditModal}
        selectedWoodchipSample={selectedWoodchipSampleData}
        addHandleWoodChipSampleDataSubmit={addHandleWoodChipSampleDataSubmit}
        dockets={dockets}
        woodchipSampleData={woodchipSampleData}
        isRetestMode={isRetestMode}
      />

      <Dialog open={retestConfirmOpen} onClose={cancelRetest} maxWidth="xs" fullWidth>
        <DialogTitle>Mark for Retest?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will create a new retest sample for <strong>#{pendingRetestRow?.wH_DocketNumber}</strong> and add it to the
            Retest queue. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRetest}>Cancel</Button>
          <Button onClick={confirmRetest} variant="contained" style={{ backgroundColor: '#f57c00', color: '#fff' }}>
            Yes, Mark for Retest
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer MC upload result */}
      <Dialog open={!!mcResultDialog} onClose={() => setMcResultDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{mcResultDialog?.type === 'success' ? 'Upload Complete' : 'Upload Failed'}</DialogTitle>
        <DialogContent dividers={mcResultDialog?.type !== 'success'}>
          {mcResultDialog?.type === 'success' ? (
            <Typography variant="body2">Uploaded all {mcResultDialog?.total} customer MC record(s) successfully.</Typography>
          ) : (
            <Box>
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                <strong>{mcResultDialog?.succeeded}</strong> of <strong>{mcResultDialog?.total}</strong> uploaded.{' '}
                <strong style={{ color: '#c62828' }}>{mcResultDialog?.failed}</strong> failed.
              </Typography>

              {mcResultDialog?.groups?.map((group, i) => (
                <Box key={i} mb={2} p={1.5} bgcolor="#fff3e0" borderRadius={4} border="1px solid #ffe0b2">
                  <Typography variant="body2" style={{ fontWeight: 600, color: '#e65100', marginBottom: 6 }}>
                    {group.dockets.length} docket(s) — {group.error}
                  </Typography>
                  <Box
                    style={{
                      maxHeight: 120,
                      overflowY: 'auto',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                    }}>
                    {group.dockets.map((docket, j) => (
                      <Chip
                        key={j}
                        label={docket}
                        size="small"
                        style={{ backgroundColor: '#fff', border: '1px solid #ffcc80' }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {mcResultDialog?.type === 'error' && mcResultDialog?.groups?.length > 0 && (
            <Button
              onClick={() => {
                const text = mcResultDialog.groups.flatMap(g => g.dockets.map(d => `${d}\t${g.error}`)).join('\n');
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                }
              }}>
              Copy Docket List
            </Button>
          )}
          <Button
            onClick={() => setMcResultDialog(null)}
            variant={mcResultDialog?.type === 'success' ? 'contained' : 'text'}
            color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!mcPreviewRows} onClose={cancelMcPreview} maxWidth="md" fullWidth>
        <DialogTitle>
          Review Customer MC Data — {mcPreviewFileName}
          <Typography variant="body2" color="textSecondary">
            {mcPreviewRows?.length ?? 0} row(s) found.
          </Typography>
        </DialogTitle>

        {mcHardErrorCount > 0 && (
          <Box px={2} py={1} m={2} bgcolor="#ffebee" borderBottom="1px solid #ffcdd2">
            <Typography variant="body2" style={{ color: '#c62828', fontWeight: 600 }}>
              {mcHardErrorCount} row(s) are missing a required Customer or CustomerMC %. Fix these in the source file and
              re-upload — this cannot be skipped.
            </Typography>
          </Box>
        )}

        {mcSoftErrorCount > 0 && (
          <Box px={2} py={1} m={2} bgcolor="#fff3e0" borderBottom="1px solid #ffe0b2">
            <Typography variant="body2" style={{ color: '#e65100', fontWeight: 600 }}>
              {mcSoftErrorCount} row(s) have invalid Net Weight / Value values (highlighted below) — these will upload as
              blank.
            </Typography>
          </Box>
        )}

        <DialogContent dividers style={{ padding: 0 }}>
          <TableContainer style={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Row</TableCell>
                  <TableCell style={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Customer</TableCell>
                  <TableCell style={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Docket #</TableCell>
                  <TableCell style={{ fontWeight: 700, backgroundColor: '#f5f5f5' }} align="right">
                    MC (%)
                  </TableCell>
                  <TableCell style={{ fontWeight: 700, backgroundColor: '#f5f5f5' }} align="right">
                    Net Wt (t)
                  </TableCell>
                  <TableCell style={{ fontWeight: 700, backgroundColor: '#f5f5f5' }} align="right">
                    Value (€)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mcPreviewRows?.map(row => {
                  const fieldStyle = field => {
                    if (row.hardErrors.some(err => err.field === field)) {
                      return { backgroundColor: '#ffcdd2', color: '#b71c1c', fontWeight: 700 };
                    }
                    if (row.softErrors.some(err => err.field === field)) {
                      return { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 };
                    }
                    return undefined;
                  };
                  const rowTitle = [...row.hardErrors, ...row.softErrors].map(err => err.message).join(' ');
                  return (
                    <TableRow key={row._rowNum} hover title={rowTitle || undefined}>
                      <TableCell>{row._rowNum}</TableCell>
                      <TableCell style={fieldStyle('customer')}>
                        {row.customer || <em style={{ color: '#c62828' }}>blank</em>}
                      </TableCell>
                      <TableCell>{row.customerDocketNumber || <em style={{ color: '#c62828' }}>blank</em>}</TableCell>
                      <TableCell align="right" style={fieldStyle('customerMC')}>
                        {row.customerMC !== null ? row.customerMC.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right" style={fieldStyle('netWeightInTonnes')}>
                        {row.netWeightInTonnes !== null ? row.netWeightInTonnes.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right" style={fieldStyle('valueInEuros')}>
                        {row.valueInEuros !== null ? row.valueInEuros.toFixed(2) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions style={{ justifyContent: 'space-between', paddingLeft: 16 }}>
          {mcSoftErrorCount > 0 ? (
            <FormControlLabel
              control={<Checkbox checked={mcAckInvalid} onChange={e => setMcAckInvalid(e.target.checked)} size="small" />}
              label={<Typography variant="body2">I understand flagged optional values will upload as blank</Typography>}
            />
          ) : (
            <span />
          )}
          <Box>
            <Button onClick={cancelMcPreview}>Cancel</Button>
            <Button
              onClick={confirmMcUpload}
              variant="contained"
              color="primary"
              disabled={mcHardErrorCount > 0 || (mcSoftErrorCount > 0 && !mcAckInvalid)}>
              Proceed — Upload {mcPreviewRows?.length ?? 0} Row(s)
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Non-dismissable upload progress */}
      <Dialog
        open={mcUploading}
        disableEscapeKeyDown
        onClose={() => {}} // ignore backdrop clicks — no dismiss during upload
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Uploading Customer MC Data</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <LinearProgress />

            <Typography variant="body2">Processing Customer MC records...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WoodChipSamplePage;
